import { Hash, Info } from 'lucide-react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { Callout } from '@/_shared/components/ui/callout';
import { Label } from '@/_shared/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { Textarea } from '@/_shared/components/ui/textarea';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

import type { ChannelOption } from '../hooks/useChannelOptions';
import { buildEachRowsBlock, SPECIAL_TOKENS } from '../hooks/useDryRun';
import { hasStepPreview } from '../lib/step-preview';
import type { MessageToken, StepStatus, TestRunResult } from '../types';
import { SlackBubble } from './SlackBubble';
import { StepCard } from './StepCard';
import { StepResult } from './StepResult';

interface SlackStepProps {
  stepNumber: number;
  channel: string;
  template: string;
  status: StepStatus;
  result: TestRunResult['slack'] | null;
  /** Dry-run error message to display inline (BAD_REQUEST from server). */
  error?: string | null;
  onChannelChange: (channel: string) => void;
  onTemplateChange: (template: string) => void;
  /** Form-validation errors — shown inline in danger once touched/submitted. */
  channelError?: string;
  templateError?: string;
  /** Mark the message field touched (on blur) so its error may show. */
  onTemplateBlur?: () => void;
  /** Real channel options from slack.listChannelBindings. */
  channelOptions: ChannelOption[];
  channelOptionsLoading?: boolean;
  /** True when admin-scoped endpoint returned FORBIDDEN for member caller. */
  channelAccessDenied?: boolean;
  /**
   * Columns discovered from the latest successful dry-run.
   * These drive the token chips so authors can insert `{{column_name}}` snippets.
   * Before any dry-run, only special tokens are shown + a hint.
   */
  columns?: string[];
  /**
   * Read-only while the RoutineBuilder agent is mid-turn (it owns the draft during its turn); the human
   * edits once the agent is idle (v2 §0). When set, the channel picker + message field are disabled and
   * the token-insert chips (write affordances) are hidden.
   */
  readOnly?: boolean;
  /** Flash the card's highlight when the agent freshly fills this step (§6.5). */
  highlight?: boolean;
}

export function SlackStep({
  stepNumber,
  channel,
  template,
  status,
  result,
  error,
  onChannelChange,
  onTemplateChange,
  channelError,
  templateError,
  onTemplateBlur,
  channelOptions,
  channelOptionsLoading,
  channelAccessDenied,
  columns,
  readOnly = false,
  highlight = false,
}: SlackStepProps): React.ReactElement {
  const { wp } = useWorkspace();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Insert text at the current caret position (or replace the selection), then restore focus.
  const insertAtCaret = (snippet: string): void => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? template.length;
    const end = el?.selectionEnd ?? template.length;
    const next = template.slice(0, start) + snippet + template.slice(end);
    onTemplateChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const caret = start + snippet.length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // Build token chips: discovered columns (from dry-run) + specials.
  const columnTokens: MessageToken[] = (columns ?? []).map((col) => ({
    token: `{{${col}}}`,
    description: col,
  }));
  const hasColumns = columnTokens.length > 0;

  // Find the display label for the currently selected channel.
  const channelLabel = channelOptions.find((c) => c.value === channel)?.label ?? channel;

  // Keep the saved channel selectable in the disabled read-only Select even when it isn't among the
  // bound options (e.g. the destination is stored as a #name while the picker lists channel IDs) — a
  // Radix <Select> renders BLANK for a value absent from its items, so seed a synthetic entry for it.
  const channelItems =
    channel && !channelOptions.some((c) => c.value === channel)
      ? [{ value: channel, label: channelLabel }, ...channelOptions]
      : channelOptions;

  return (
    <StepCard
      stepNumber={stepNumber}
      kind="Slack"
      title="Slack message"
      icon={Hash}
      highlight={highlight}
      hasError={!!error}
      footerStale={status === 'stale'}
      footer={
        hasStepPreview(status, result) ? (
          <StepResult status={status}>
            {result != null && <SlackBubble channel={channelLabel} message={result.renderedMessage} />}
          </StepResult>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="slack-channel">Channel</Label>
        {!channelOptionsLoading && channelOptions.length === 0 && !readOnly ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            {channelAccessDenied ? (
              <>Only admins can manage channel bindings. Contact your workspace admin to configure Slack channels.</>
            ) : (
              <>
                No connected Slack channels.{' '}
                <Link
                  to={wp('/settings/integrations/slack')}
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  Connect Slack in Settings
                </Link>{' '}
                to enable channel delivery.
              </>
            )}
          </div>
        ) : (
          // Read-only editor (agent mid-turn): the picker is shown DISABLED rather than as static text,
          // so the field reads consistently across states (see channelItems for the value fallback).
          <Select value={channel} onValueChange={onChannelChange} disabled={Boolean(channelOptionsLoading) || readOnly}>
            <SelectTrigger id="slack-channel" aria-invalid={!!channelError} className="w-full">
              <SelectValue placeholder={channelOptionsLoading ? 'Loading…' : 'Select a channel'} />
            </SelectTrigger>
            <SelectContent>
              {channelItems.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <p className="text-xs text-muted-foreground">
          Pick one of the{' '}
          <Link
            to={wp('/settings/integrations/slack')}
            className="text-primary underline underline-offset-2 hover:text-primary/80"
          >
            connected channels
          </Link>
          .
        </p>
        {channelError && (
          <p className="text-xs font-medium text-danger" role="alert">
            {channelError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <Label htmlFor="slack-template">Message</Label>
          {/* Token reference + insert chips live behind an (i) popover so they don't crowd the field. */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Message tokens and insert helpers"
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <Info className="size-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 text-xs">
              <p className="mb-2 leading-relaxed text-muted-foreground">
                Scalar tokens fill once; the <code className="font-mono">{'{{#each rows}}'}</code> block repeats per row
                inside the single message.
              </p>
              {readOnly ? (
                <p className="text-muted-foreground">Editable when the agent is idle.</p>
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Insert:</span>
                  {hasColumns ? (
                    columnTokens.map((t) => (
                      <button
                        key={t.token}
                        type="button"
                        onClick={() => insertAtCaret(t.token)}
                        title={t.description}
                        className="cursor-pointer rounded-full border border-border bg-card px-2 py-0.5 font-mono text-xs font-medium text-foreground focus-ring transition-colors hover:border-primary hover:bg-muted"
                      >
                        {t.token}
                      </button>
                    ))
                  ) : (
                    <span className="rounded-full bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground italic">
                      Dry run to discover columns
                    </span>
                  )}
                  {SPECIAL_TOKENS.map((t) => (
                    <button
                      key={t.token}
                      type="button"
                      onClick={() => insertAtCaret(t.token)}
                      title={t.description}
                      className="cursor-pointer rounded-full border border-border bg-card px-2 py-0.5 font-mono text-xs font-medium text-foreground focus-ring transition-colors hover:border-primary hover:bg-muted"
                    >
                      {t.token}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => insertAtCaret(buildEachRowsBlock(columns ?? []))}
                    title={
                      (columns ?? []).length > 0
                        ? 'Repeat a line per row — uses discovered columns'
                        : 'Repeat a line per row — dry run first to use real column names'
                    }
                    className="cursor-pointer rounded-full border border-dashed border-border px-2 py-0.5 font-mono text-xs font-medium text-foreground focus-ring transition-colors hover:border-primary hover:bg-muted"
                  >
                    {'{{#each rows}} … {{/each}}'}
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
        <Textarea
          id="slack-template"
          ref={textareaRef}
          value={template}
          onChange={(e) => onTemplateChange(e.target.value)}
          onBlur={onTemplateBlur}
          aria-invalid={!!templateError}
          rows={4}
          className="max-h-[25rem] font-mono text-[0.8125rem]"
          readOnly={readOnly}
        />
        {templateError && (
          <p className="text-xs font-medium text-danger" role="alert">
            {templateError}
          </p>
        )}
      </div>

      {/* role="alert" so the step-specific dry-run reason is announced (the top banner is only a count). */}
      {error && (
        <Callout variant="error" role="alert">
          {error}
        </Callout>
      )}
    </StepCard>
  );
}
